import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { addDays, parseISO } from 'date-fns';
import { Icon } from '@/components/common/Icon';
import { Card } from '@/components/ui/card';
import { VisitQuickActions } from '@/components/practitioner/shared/VisitQuickActions';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import {
  buildAppointmentStatusTranslationKey,
  buildTherapyTypeTranslationKey,
} from '@/constants/appointmentOptions';
import { useAppointmentsQuery } from '@/hooks/useAppointmentsQuery';
import { formatDateToFullWeekdayDate, formatDateToIsoDateOnly } from '@/utils/dateFormatting';
import { groupUpcomingVisits, quickActionsForVisit, UPCOMING_VISIT_DAYS } from './upcomingVisits';

const APPOINTMENT_LINK_PREFIX = APPLICATION_ROUTES.workspaceDashboard + '?appointment=';

interface UpcomingVisitsCardProps {
  className?: string;
}

export function UpcomingVisitsCard({ className }: UpcomingVisitsCardProps) {
  const { t } = useTranslation(['dashboard', 'appointments']);
  const appointmentsQuery = useAppointmentsQuery();
  const today = new Date();
  const days = groupUpcomingVisits(
    appointmentsQuery.data === undefined ? [] : appointmentsQuery.data,
    today
  );
  const todayDate = formatDateToIsoDateOnly(today);
  const tomorrowDate = formatDateToIsoDateOnly(addDays(today, 1));

  function dayLabel(date: string): string {
    if (date === todayDate) {
      return t('dashboard:upcomingVisits.today');
    }
    if (date === tomorrowDate) {
      return t('dashboard:upcomingVisits.tomorrow');
    }
    return formatDateToFullWeekdayDate(parseISO(date));
  }

  return (
    <Card as="section" padding="sm" className={className}>
      <div className="flex items-center gap-2">
        <span className="w-8 h-8 rounded-lg bg-zoovpalelavender text-zooprimary flex items-center justify-center shrink-0">
          <Icon name="Calendar" />
        </span>
        <h2 className="text-base font-bold text-zooindigo flex-1 min-w-0">
          {t('dashboard:upcomingVisits.title')}
        </h2>
      </div>
      <p className="text-xs text-zoomuted mt-1 mb-3">
        {t('dashboard:upcomingVisits.subtitle', { days: UPCOMING_VISIT_DAYS })}
      </p>
      {appointmentsQuery.isLoading ? null : days.length === 0 ? (
        <p className="text-sm text-zoomuted">{t('dashboard:upcomingVisits.empty')}</p>
      ) : (
        <div className="flex flex-col gap-3 max-h-[32rem] overflow-y-auto -mx-1 px-1">
          {days.map(function renderDay(day) {
            return (
              <section key={day.date}>
                <h3 className="text-[11px] font-bold uppercase tracking-wide text-zoomuted mb-1">
                  {dayLabel(day.date)}
                </h3>
                <ul className="divide-y divide-zooborder">
                  {day.visits.map(function renderVisit(visit) {
                    return (
                      <li key={visit.id}>
                        <Link
                          to={APPOINTMENT_LINK_PREFIX + visit.id}
                          className="block py-2 rounded-lg hover:bg-zoovpalelavender hover:bg-opacity-40 px-1 -mx-1 transition-colors"
                        >
                          <span className="flex items-baseline gap-2">
                            <span className="text-[13px] font-bold text-zooprimary shrink-0">
                              {visit.startTime}
                            </span>
                            <span className="text-[13px] font-semibold text-zooindigo truncate">
                              {visit.patientName}
                              {visit.clientName === '' ? '' : ' · ' + visit.clientName}
                            </span>
                          </span>
                          <span className="block text-[11px] text-zoomuted truncate">
                            {t(buildTherapyTypeTranslationKey(visit.therapyType)) +
                              ' · ' +
                              t(buildAppointmentStatusTranslationKey(visit.status))}
                          </span>
                        </Link>
                        <VisitQuickActions
                          appointmentId={visit.id}
                          patientId={visit.patientId}
                          actions={quickActionsForVisit(visit, today)}
                        />
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </Card>
  );
}
